import React, { useState, useMemo } from 'react';
import { RenovationInputs, QualityLevel } from '../../types';
import { calculateRenovationCosts, getReferenceTableData } from '../../utils/renovationCalculations';

const Sanierung: React.FC = () => {
  const [inputs, setInputs] = useState<RenovationInputs>({
    wohnflaeche: 75,
    bodenflaeche: 75,
    fliesenbodenflaeche: 0,
    wandflaeche: 150,
    fassadenflaeche: 0,
    dachflaeche: 0,
    anzahlZimmer: 3,
    anzahlBad: 1,
    anzahlKueche: 1,
    anzahlHeizkoerper: 4,
    anzahlTueren: 4,
    anzahlFenster: 4,
    anzahlRollos: 4,
    heizungErneuern: true,
    elektrikErneuern: true,
    bodenErneuern: true,
    malenErneuern: true,
    wasserleitungenBadErneuern: true,
    wasserleitungenKuecheErneuern: true,
    abrissKosten: 0,
    planungProzent: 2,
    statikKosten: 0,
    risikozuschlag: 15,
  });

  const [quality, setQuality] = useState<QualityLevel>('mittel');

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;
    setInputs(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : parseFloat(value) || 0
    }));
  };

  const calculatedCosts = useMemo(() => {
    return calculateRenovationCosts(inputs, quality);
  }, [inputs, quality]);

  const referenceTableData = getReferenceTableData();

  // Component for input fields with unit
  const InputField = ({ label, unit, value, onChange, name, helpText = "" }: any) => (
    <div className="flex justify-between items-center">
      <div className="flex-grow pr-4">
        <label htmlFor={name} className="text-sm font-medium text-gray-700 block">{label}</label>
        {helpText && <p className="text-xs text-gray-500">{helpText}</p>}
      </div>
      <div className="relative rounded-md shadow-sm w-32 flex-shrink-0">
        <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
          <span className="text-gray-500 sm:text-sm">{unit}</span>
        </div>
        <input
          type="number"
          name={name}
          id={name}
          value={value === 0 ? '' : value}
          onChange={onChange}
          className="block w-full rounded-md border-gray-300 py-1 pl-12 text-right focus:border-blue-500 focus:ring-blue-500"
          placeholder="0"
          onFocus={(e) => e.target.select()}
        />
      </div>
    </div>
  );

  // Component for editable cost inputs
  const EditableCostInput = ({ label, unit, value, onChange, name }: any) => (
    <div className="flex justify-between items-center">
      <label htmlFor={name} className="text-sm font-medium text-gray-700">{label}</label>
      <div className="relative rounded-md shadow-sm w-32">
        <input
          type="number"
          name={name}
          id={name}
          value={value === 0 ? '' : value}
          onChange={onChange}
          className="block w-full rounded-md border-gray-300 py-1 pl-7 text-right focus:border-blue-500 focus:ring-blue-500"
          placeholder="0"
          onFocus={(e) => e.target.select()}
        />
        <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3">
          <span className="text-gray-500 sm:text-sm">{unit}</span>
        </div>
      </div>
    </div>
  );

  // Component for toggle switches
  const ToggleSwitch = ({ label, checked, onChange, name }: any) => (
    <div className="flex items-center justify-between">
      <span className="text-sm font-medium text-gray-700">{label}</span>
      <label className="relative inline-flex items-center cursor-pointer">
        <input
          type="checkbox"
          name={name}
          id={name}
          checked={checked}
          onChange={onChange}
          className="sr-only peer"
        />
        <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
      </label>
    </div>
  );

  // Quality selector component
  const QualitySelector = ({ selectedQuality, setQuality }: any) => {
    const levels = { basis: "Basis", mittel: "Mittel", premium: "Premium" } as const;
    return (
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">Qualitätsniveau</label>
        <div className="flex bg-gray-200 rounded-lg p-1">
          {(Object.keys(levels) as Array<keyof typeof levels>).map((level) => (
            <button
              key={level}
              onClick={() => setQuality(level)}
              className={`w-1/3 py-2 px-2 text-center rounded-md text-sm font-medium transition-colors duration-300 focus:outline-none ${
                selectedQuality === level ? 'bg-white text-blue-600 shadow' : 'bg-transparent text-gray-600 hover:bg-gray-300'
              }`}
            >
              {levels[level]}
            </button>
          ))}
        </div>
      </div>
    );
  };

  // Printer icon component
  const PrinterIcon = ({ className }: any) => (
    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>
      <polyline points="6 9 6 2 18 2 18 9" />
      <path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2" />
      <rect x="6" y="14" width="12" height="8" />
    </svg>
  );

  return (
    <div className="tab-content active">
      <div className="max-w-7xl mx-auto">
        <header className="text-center mb-8">
          <h1 className="text-3xl md:text-4xl font-bold text-gray-800">Automatischer Sanierungsrechner</h1>
          <p className="text-gray-500 mt-2">Geben Sie die Kenndaten Ihrer Immobilie ein, um die Kosten zu schätzen.</p>
        </header>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-8">
          {/* Input fields section */}
          <div className="lg:col-span-3 bg-white p-6 md:p-8 rounded-2xl shadow-lg">
            <h2 className="text-xl font-semibold text-gray-700 mb-6">1. Objektdaten & Qualität</h2>
            <div className="space-y-4">
              <QualitySelector selectedQuality={quality} setQuality={setQuality} />
              
              <div className="pt-4 border-t">
                <h3 className="text-lg font-medium text-gray-800 mb-3">Flächenangaben</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-3">
                  <InputField label="Wohnfläche" unit="m²" name="wohnflaeche" value={inputs.wohnflaeche} onChange={handleInputChange} />
                  <InputField label="Bodenfläche" unit="m²" name="bodenflaeche" value={inputs.bodenflaeche} onChange={handleInputChange} />
                  <InputField label="Fliesenbodenfläche" unit="m²" name="fliesenbodenflaeche" value={inputs.fliesenbodenflaeche} onChange={handleInputChange} />
                  <InputField label="Wandfläche" unit="m²" name="wandflaeche" value={inputs.wandflaeche} onChange={handleInputChange} helpText="(wird geschätzt falls leer)" />
                  <InputField label="Fassadenfläche (Anteil)" unit="m²" name="fassadenflaeche" value={inputs.fassadenflaeche} onChange={handleInputChange} />
                  <InputField label="Dachfläche (Anteil)" unit="m²" name="dachflaeche" value={inputs.dachflaeche} onChange={handleInputChange} />
                  <InputField label="Anzahl Zimmer" unit="Stk." name="anzahlZimmer" value={inputs.anzahlZimmer} onChange={handleInputChange} />
                </div>
              </div>

              <div className="pt-4 border-t">
                <h3 className="text-lg font-medium text-gray-800 mb-3">Anzahl Bauteile</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-3">
                  <InputField label="Bäder" unit="Stk." name="anzahlBad" value={inputs.anzahlBad} onChange={handleInputChange} />
                  <InputField label="Küchen" unit="Stk." name="anzahlKueche" value={inputs.anzahlKueche} onChange={handleInputChange} />
                  <InputField label="Heizkörper" unit="Stk." name="anzahlHeizkoerper" value={inputs.anzahlHeizkoerper} onChange={handleInputChange} />
                  <InputField label="Türen" unit="Stk." name="anzahlTueren" value={inputs.anzahlTueren} onChange={handleInputChange} />
                  <InputField label="Fenster" unit="Stk." name="anzahlFenster" value={inputs.anzahlFenster} onChange={handleInputChange} />
                  <InputField label="Rollos" unit="Stk." name="anzahlRollos" value={inputs.anzahlRollos} onChange={handleInputChange} />
                </div>
              </div>

              <div className="pt-4 border-t">
                <h3 className="text-lg font-medium text-gray-800 mb-3">Optionale Gewerke</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-3">
                  <ToggleSwitch label="Heizung komplett erneuern?" name="heizungErneuern" checked={inputs.heizungErneuern} onChange={handleInputChange} />
                  <ToggleSwitch label="Elektrik komplett erneuern?" name="elektrikErneuern" checked={inputs.elektrikErneuern} onChange={handleInputChange} />
                  <ToggleSwitch label="Boden komplett erneuern?" name="bodenErneuern" checked={inputs.bodenErneuern} onChange={handleInputChange} />
                  <ToggleSwitch label="Wände/Decken malen?" name="malenErneuern" checked={inputs.malenErneuern} onChange={handleInputChange} />
                  <ToggleSwitch label="Wasserleitungen Bad?" name="wasserleitungenBadErneuern" checked={inputs.wasserleitungenBadErneuern} onChange={handleInputChange} />
                  <ToggleSwitch label="Wasserleitungen Küche?" name="wasserleitungenKuecheErneuern" checked={inputs.wasserleitungenKuecheErneuern} onChange={handleInputChange} />
                </div>
              </div>

              <div className="pt-4 border-t">
                <h3 className="text-lg font-medium text-gray-800 mb-3">Baunebenkosten & Zuschläge</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-3">
                  <EditableCostInput label="Abriss & Entsorgung" unit="€" name="abrissKosten" value={inputs.abrissKosten} onChange={handleInputChange} />
                  <EditableCostInput label="Planung & Baubetreuung" unit="%" name="planungProzent" value={inputs.planungProzent} onChange={handleInputChange} />
                  <EditableCostInput label="Statik / Gutachten" unit="€" name="statikKosten" value={inputs.statikKosten} onChange={handleInputChange} />
                  <EditableCostInput label="Risikopuffer" unit="%" name="risikozuschlag" value={inputs.risikozuschlag} onChange={handleInputChange} />
                </div>
              </div>
            </div>
          </div>

          {/* Cost summary section */}
          <div className="lg:col-span-2 space-y-6">
            <div className="bg-blue-600 text-white p-6 md:p-8 rounded-2xl shadow-lg">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-xl font-semibold text-blue-100">2. Kostenschätzung</h2>
                <button onClick={() => window.print()} className="p-2 rounded-full bg-blue-500 hover:bg-blue-400 transition-colors">
                  <PrinterIcon className="h-5 w-5 text-white" />
                </button>
              </div>
              <div className="space-y-2">
                <div className="flex justify-between items-baseline">
                  <span className="text-blue-200">Summe Gewerke</span>
                  <span className="text-lg font-medium">
                    {new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(calculatedCosts.subtotalGewerke)}
                  </span>
                </div>
                <div className="flex justify-between items-baseline">
                  <span className="text-blue-200">Baunebenkosten</span>
                  <span className="text-lg font-medium">
                    {new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(calculatedCosts.subtotalNebenkosten)}
                  </span>
                </div>
                <div className="flex justify-between items-baseline border-t border-blue-500 pt-2 mt-1">
                  <span className="text-blue-200">Zwischensumme</span>
                  <span className="text-2xl font-medium">
                    {new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(calculatedCosts.subtotal)}
                  </span>
                </div>
                <div className="flex justify-between items-baseline">
                  <span className="text-blue-200 text-sm">+ Risikozuschlag ({inputs.risikozuschlag}%)</span>
                  <span className="text-lg font-medium">
                    {new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(calculatedCosts.risikoValue)}
                  </span>
                </div>
                <div className="border-t-2 border-blue-400 my-3"></div>
                <div className="flex justify-between items-center">
                  <span className="text-xl font-bold">GESAMT</span>
                  <span className="text-4xl font-bold">
                    {new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(calculatedCosts.total)}
                  </span>
                </div>
                <div className="flex justify-between items-baseline pt-4 border-t border-blue-500">
                  <span className="text-blue-200">Kosten pro m²</span>
                  <span className="text-xl font-medium">
                    {new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(calculatedCosts.kostenProQm)}
                  </span>
                </div>
                <div className="text-xs text-blue-200 mt-2">
                  Geschätzte Wandfläche: {calculatedCosts.geschaetzteWandflaeche} m²
                </div>
              </div>
            </div>

            {/* Detailed cost breakdown */}
            <div className="bg-white p-6 rounded-2xl shadow-lg">
              <h3 className="text-lg font-semibold text-gray-700 mb-4">Detaillierte Kostenaufstellung</h3>
              <div className="space-y-1 max-h-96 overflow-y-auto">
                {calculatedCosts.costItems
                  .filter(item => item.value > 0)
                  .map((item, index) => (
                    <div key={index} className="flex justify-between items-baseline py-1 text-sm">
                      <span className="text-gray-600">{item.label}</span>
                      <span className="font-mono text-gray-800">
                        {new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(item.value)}
                      </span>
                    </div>
                  ))}
              </div>
            </div>
          </div>
        </div>

        {/* Reference cost table */}
        <div className="mt-8 bg-white p-6 md:p-8 rounded-2xl shadow-lg">
          <h2 className="text-xl font-semibold text-gray-700 mb-6">Referenzkosten-Tabelle (Stand: Mitte 2025, Region Bayern)</h2>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Position
                  </th>
                  <th scope="col" className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Basis
                  </th>
                  <th scope="col" className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Mittel
                  </th>
                  <th scope="col" className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Premium
                  </th>
                  <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Anmerkungen
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {referenceTableData.map((item, index) => (
                  <tr key={index} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                    <td className="px-4 py-2 text-sm text-gray-900 font-medium">{item.label}</td>
                    <td className="px-4 py-2 text-sm text-gray-600 text-right font-mono">
                      {typeof item.basis === 'number' 
                        ? new Intl.NumberFormat('de-DE').format(item.basis) 
                        : item.basis}
                    </td>
                    <td className="px-4 py-2 text-sm text-gray-600 text-right font-mono">
                      {typeof item.mittel === 'number' 
                        ? new Intl.NumberFormat('de-DE').format(item.mittel) 
                        : item.mittel}
                    </td>
                    <td className="px-4 py-2 text-sm text-gray-600 text-right font-mono">
                      {item.premium}
                    </td>
                    <td className="px-4 py-2 text-xs text-gray-500">{item.notes}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          
          <div className="mt-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
            <p className="text-sm text-gray-700">
              <strong>Hinweis:</strong> Diese Kostenschätzung basiert auf durchschnittlichen Preisen in Bayern (Stand: Mitte 2025). 
              Die tatsächlichen Kosten können je nach Region, Ausführung und individuellen Anforderungen abweichen.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Sanierung;